import React, { useEffect, useRef } from "react";
import styled, { css } from "styled-components";

// Context
import { useFormSelectDialog } from "core/FormSelectDialog/context/useFormSelectDialog";
import {
    focusPrevOption,
    focusNextOption,
    focusSelectedOption,
} from "../utils";

// styles
import colors from "core/colors";
import { convertPxToRem } from "frontend/components/helpers";

const dialogDropShadow = `0px ${convertPxToRem(3)} ${convertPxToRem(7)} rgb(0 0 0 / 24%)`;
const dialogDropUpShadow = `0px 0px ${convertPxToRem(7)} rgb(0 0 0 / 24%)`;

export const DialogStyled = styled.div`
    position: relative;
    > div {
        margin: 0px;
        padding: 0px;
        width: ${({ $dialogWidth }) => convertPxToRem($dialogWidth)};
        background-color: ${colors.primary.snow};
        color: ${colors.secondary.oolong};
        box-shadow: ${dialogDropShadow};
        border: 1px solid ${colors.primary.diamond};

        position: absolute;
        z-index: 10;

        ${({ $dialogPlacement, $dialogOffset = 0 }) => {
            let verticalPos = `top: ${convertPxToRem($dialogOffset)}`;
            let boxShadow = dialogDropShadow;

            if ($dialogPlacement === "top") {
                verticalPos = `bottom: ${convertPxToRem($dialogOffset)}`;
                boxShadow = dialogDropUpShadow;
            }

            return css`
                ${verticalPos};
                box-shadow: ${boxShadow};
            `;
        }}

        ${({ $dialogAlignment }) => {
            let styles;

            switch ($dialogAlignment) {
                case "center":
                    styles = css`
                        left: 50%;
                        transform: translate(-50%, 0);
                    `;
                    break;
                case "right":
                    styles = css`
                        right: 0rem;
                    `;
                    break;
                default:
                    styles = css`
                        left: 0rem;
                    `;
                    break;
            }

            return styles;
        }}
    }
`;

// components
import DialogOption from "./DialogOption";

const Dialog = ({ children }) => {
    const {
        id,
        options,
        isOpen,
        dialogAlignment,
        dialogPlacement,
        dialogOffset,
        dialogWidth,
        hasOpenedBefore,
        selectedOption,
    } = useFormSelectDialog();

    const optionRefs = useRef([]);

    // side effects
    useEffect(() => {
        const onKeyDown = event => {
            switch (event.key) {
                case "ArrowUp":
                    event.preventDefault();
                    focusPrevOption(optionRefs);
                    break;
                case "ArrowDown":
                    event.preventDefault();
                    focusNextOption(optionRefs);
                    break;
                default:
                    return;
            }
        };

        window.addEventListener("keydown", onKeyDown);

        return () => {
            window.removeEventListener("keydown", onKeyDown);
        };
    }, []);

    useEffect(() => {
        if (isOpen && hasOpenedBefore) {
            focusSelectedOption(optionRefs, selectedOption, options);
        }
    }, [hasOpenedBefore, isOpen]);

    const Component = (
        <DialogStyled
            $dialogAlignment={dialogAlignment}
            $dialogPlacement={dialogPlacement}
            $dialogOffset={dialogOffset}
            $dialogWidth={dialogWidth}
        >
            <div
                role="listbox"
                aria-labelledby={`${id}-dropdown-button`}
                hidden={!isOpen}
                tabIndex={0} // Make listbox focusable
            >
                {options.map((option, idx) => {
                    return (
                        <DialogOption
                            key={option.id}
                            option={option}
                            optionRef={el => (optionRefs.current[idx] = el)}
                        />
                    );
                })}
            </div>
        </DialogStyled>
    );

    // Place Dialog on bottom for position anchor
    if (dialogPlacement === "bottom") {
        return (
            <>
                {children}
                {Component}
            </>
        );
    }

    // Place Dialog on top for position anchor
    return (
        <>
            {Component}
            {children}
        </>
    );
};

export default Dialog;

import PropTypes from "prop-types";

// prop-types
Dialog.propTypes = {
    children: PropTypes.any,
};
